import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IFundDetails } from '../../interfaces/fund-details/fund-details';
import { FundDetailsApiResponse } from '../../interfaces/fund-details/fund-details-api-response';
import {
  IFinancialData,
  IFinancialDataApiResponse,
} from '../../interfaces/fund-details/financial-data';
import { API_ENDPOINTS } from '../../utils/api-endpoints.const';
import { FundStatus } from '../../enums/fund-status';

@Injectable({
  providedIn: 'root',
})
export class FundDetailsService {
  private readonly http = inject(HttpClient);

  readonly isLoading = signal<boolean>(false);
  /** Resolved by ViewFundDetails before the fund-details request fires (routed
   * uuid or, when previewed in an off-canvas, its `fundUuid` input) — so tabs
   * can read it synchronously instead of waiting on `fundData()` to load. */
  readonly fundUuid = signal<string | null>(null);
  readonly fundData = signal<IFundDetails | undefined>(undefined);
  readonly financialData = signal<IFinancialData | undefined>(undefined);
  readonly isPreviewMode = signal<boolean>(false);
  /** True once the fund has at least one succeeded investment. Gates the Unit
   * Management tab and adding restricted units. Derived from the investments
   * list (see FundInvestments.hasSucceededInvestment). */
  readonly hasSuccessfulInvestment = signal<boolean>(false);

  /**
   * Get fund details by UUID
   */
  getFundDetails(uuid: string): Observable<IFundDetails> {
    this.isLoading.set(true);
    return this.http
      .get<FundDetailsApiResponse>(API_ENDPOINTS.FUND_DETAILS(uuid))
      .pipe(map(response => response.data));
  }

  /**
   * Check if fund is drafted by UUID
   */
  isFundDrafted(uuid: string): Observable<boolean> {
    return this.getFundDetails(uuid).pipe(
      map(fund => fund.state?.value === FundStatus.DRAFTED)
    );
  }

  /**
   * Get financial data by UUID
   */
  getFinancialData(uuid: string): Observable<IFinancialData> {
    this.isLoading.set(true);
    return this.reloadFinancialData(uuid);
  }

  /**
   * Financial data fetch without the page-level loading side effect. Use for
   * background refreshes (e.g. after an unrestrict changes the restricted-units
   * total) where flashing the full-page skeleton would be jarring.
   */
  reloadFinancialData(uuid: string): Observable<IFinancialData> {
    return this.http
      .get<IFinancialDataApiResponse>(API_ENDPOINTS.FUND_FINANCIAL(uuid))
      .pipe(map(response => response.data));
  }

  /**
   * Set preview mode state
   */
  setPreviewMode(isPreview: boolean): void {
    this.isPreviewMode.set(isPreview);
  }
}
